import React, { useState } from 'react';
import { ChatBubbleLeftRightIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { i18nService } from '../../services/i18n';
import { setPlatformBaseUrl } from '../../constants/auth';

interface InitPageProps {
  onComplete: () => void;
}

type VerifyStatus = 'idle' | 'verifying' | 'success' | 'failed';

const InitPage: React.FC<InitPageProps> = ({ onComplete }) => {
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState<VerifyStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleVerify = async () => {
    const trimmed = url.trim().replace(/\/+$/, '');
    if (!trimmed) return;

    setStatus('verifying');
    setErrorMessage('');

    try {
      // Check if boclaw-web backend is reachable
      const backendCheck = await fetch('/healthz').catch(() => null);
      if (!backendCheck || !backendCheck.ok) {
        throw new Error('无法连接到 boclaw-web 后端服务，请确认服务已启动');
      }

      // Save URL to server KV store
      const saveRes = await fetch('/api/platform/url', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: trimmed }),
      });

      if (!saveRes.ok) {
        const errorData = await saveRes.json().catch(() => ({}));
        const detail = errorData.error || `HTTP ${saveRes.status}`;
        if (saveRes.status === 404) {
          throw new Error(`无法连接到 boclaw-web 后端服务 (${detail})，请确认服务已启动`);
        }
        throw new Error(`保存平台地址失败: ${detail}`);
      }

      // Verify connectivity
      const healthRes = await fetch('/api/platform/health');

      if (!healthRes.ok) {
        const errorData = await healthRes.json().catch(() => ({}));
        throw new Error(errorData.error || `Health check failed (${healthRes.status})`);
      }

      setStatus('success');
    } catch (error) {
      setStatus('failed');
      setErrorMessage(error instanceof Error ? error.message : i18nService.t('initFailed'));
    }
  };

  const handleContinue = () => {
    const trimmed = url.trim().replace(/\/+$/, '');
    setPlatformBaseUrl(trimmed);
    onComplete();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (status === 'success') {
        handleContinue();
      } else if (status !== 'verifying') {
        void handleVerify();
      }
    }
  };

  return (
    <div className="h-screen flex items-center justify-center dark:bg-claude-darkBg bg-claude-bg">
      <div className="flex flex-col items-center space-y-8 max-w-md w-full px-6">
        {/* Logo */}
        <div className="flex flex-col items-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-claude-accent to-claude-accentHover flex items-center justify-center shadow-glow-accent">
            <ChatBubbleLeftRightIcon className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-semibold dark:text-claude-darkText text-claude-text">
            {i18nService.t('initTitle')}
          </h1>
          <p className="text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary text-center">
            {i18nService.t('initSubtitle')}
          </p>
        </div>

        {/* URL Input */}
        <div className="w-full space-y-4">
          <input
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              if (status === 'success' || status === 'failed') {
                setStatus('idle');
                setErrorMessage('');
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder={i18nService.t('initUrlPlaceholder')}
            className="w-full rounded-xl border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-claude-surface px-4 py-3 text-sm dark:text-claude-darkText text-claude-text outline-none focus:border-claude-accent transition-colors"
            disabled={status === 'verifying'}
          />

          {/* Status message */}
          {status === 'success' && (
            <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
              <CheckCircleIcon className="h-5 w-5 shrink-0" />
              <span>{i18nService.t('initSuccess')}</span>
            </div>
          )}
          {status === 'failed' && (
            <div className="flex items-center gap-2 text-sm text-red-500 dark:text-red-400">
              <XCircleIcon className="h-5 w-5 shrink-0" />
              <span>{errorMessage || i18nService.t('initFailed')}</span>
            </div>
          )}

          {/* Action button */}
          {status === 'success' ? (
            <button
              onClick={handleContinue}
              className="w-full rounded-xl bg-claude-accent hover:bg-claude-accentHover text-white py-3 text-sm font-medium transition-colors shadow-md"
            >
              {i18nService.t('initContinue')}
            </button>
          ) : (
            <button
              onClick={() => { void handleVerify(); }}
              disabled={!url.trim() || status === 'verifying'}
              className="w-full rounded-xl bg-claude-accent hover:bg-claude-accentHover text-white py-3 text-sm font-medium transition-colors shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === 'verifying' ? i18nService.t('initVerifying') : i18nService.t('initVerifyBtn')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InitPage;
